/**
 * Iter 67ez — Per-booking "Fiscalize" button (Albania EasyPos).
 *
 * Self-hides unless the booking belongs to a vendor with EasyPos enabled.
 * If the booking already has fiscal_nivf set, shows a green compliance chip
 * with the NIVF + NSLF instead of the action button.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Receipt, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

let _CACHED_CFG = null;
let _CACHED_CFG_AT = 0;
let _CFG_INFLIGHT = null;

async function _fetchVendorEasyPosConfig() {
  const now = Date.now();
  if (_CACHED_CFG_AT && now - _CACHED_CFG_AT < 30_000) return _CACHED_CFG;
  if (_CFG_INFLIGHT) return _CFG_INFLIGHT;
  _CFG_INFLIGHT = api.get("/vendor/easypos/config")
    .then((res) => { _CACHED_CFG = res.data; return res.data; })
    .catch(() => { _CACHED_CFG = null; return null; })
    .finally(() => { _CACHED_CFG_AT = Date.now(); _CFG_INFLIGHT = null; });
  return _CFG_INFLIGHT;
}

export default function BookingFiscalizeButton({ booking, onChanged }) {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [includeDeposit, setIncludeDeposit] = useState(false);

  useEffect(() => {
    let active = true;
    _fetchVendorEasyPosConfig().then((c) => { if (active) setCfg(c); });
    return () => { active = false; };
  }, []);

  // Hide entirely if vendor hasn't enabled fiscalization.
  if (!cfg?.enabled) return null;

  // Already fiscalized → show compliance chip.
  if (booking?.fiscal_status === "succeeded" && (booking?.fiscal_nivf || booking?.fiscal_nslf)) {
    return (
      <div
        className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[11px] font-semibold"
        title={`NIVF ${booking.fiscal_nivf || ""} · NSLF ${booking.fiscal_nslf || ""}`}
        data-testid={`fiscalized-chip-${booking.id}`}
      >
        <CheckCircle2 className="h-3 w-3" />
        Fiscalized
        {booking.fiscal_nivf && <span className="font-mono text-[10px] text-emerald-700">· {String(booking.fiscal_nivf).slice(0, 12)}</span>}
      </div>
    );
  }

  // Pending → show in-flight chip.
  if (booking?.fiscal_status === "pending") {
    return (
      <div
        className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[11px] font-semibold"
        data-testid={`fiscalize-pending-chip-${booking.id}`}
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        Fiscalization pending
      </div>
    );
  }

  async function submit() {
    setSubmitting(true);
    try {
      await api.post(`/api/bookings/${booking.id}/fiscalize`, {
        payment_method: paymentMethod,
        include_deposit: includeDeposit,
      });
      toast.success("Fiscalization job queued — your relay agent will process it shortly");
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  }

  const showFailed = booking?.fiscal_status === "failed";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant={showFailed ? "destructive" : "outline"}
          className="h-7 text-[11px] rounded-full mt-1.5"
          data-testid={`fiscalize-btn-${booking.id}`}
        >
          <Receipt className="h-3 w-3 mr-1" />
          {showFailed ? "Retry fiscalization" : "Fiscalize"}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="fiscalize-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-red-600" /> Send to easyPos</DialogTitle>
          <DialogDescription>
            Queue this booking for fiscalization through your local easyPos installation.
            Your relay agent will pick it up within seconds and register the receipt with the tax authority.
          </DialogDescription>
        </DialogHeader>
        {showFailed && booking?.fiscal_last_error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800 flex items-start gap-2" data-testid="fiscalize-previous-error">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Previous attempt failed:</strong>
              <div className="font-mono">{booking.fiscal_last_error}</div>
            </div>
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label>Payment method</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger data-testid="fiscalize-payment-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CASH">Cash</SelectItem>
                <SelectItem value="CARD">Card</SelectItem>
                <SelectItem value="ACCOUNT">Bank transfer</SelectItem>
                <SelectItem value="OTHER">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {Number(booking?.deposit || 0) > 0 && (
            <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border bg-neutral-50">
              <Checkbox
                checked={includeDeposit}
                onCheckedChange={(v) => setIncludeDeposit(!!v)}
                data-testid="fiscalize-include-deposit"
              />
              <div className="text-xs">
                <div className="font-semibold">Also fiscalize the security deposit (€{Number(booking.deposit).toFixed(2)})</div>
                <div className="text-neutral-500">Queues a second receipt for the deposit. Skip if the deposit is refundable in full.</div>
              </div>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="fiscalize-confirm">
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Queue fiscalization
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
